import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule, PLATFORM_ID } from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppLayoutModule } from "./layout/app.layout.module";
import { AppConfigModule } from "./layout/config/app.config.module";
import { NgxsModule } from "@ngxs/store";
import { EventsState } from "./features/events/state/events.state";
import { environment } from "../environments/environment";
import { NgxsLoggerPluginModule } from "@ngxs/logger-plugin";
import { NgxsStoragePluginModule } from "@ngxs/storage-plugin";
import { AUTH_STATE, STORAGE_STATE } from "../state";
import { provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import { GraphQLModule } from "./shared/graphql/graphql.module";
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers, PLAYER_SELF_STATE } from "./reducers";
import { LegalModule } from "./features/legal/legal.module";
import { AngularSvgIconModule } from "angular-svg-icon";
import { GoogleLoginProvider, SocialAuthServiceConfig } from "@abacritt/angularx-social-login";
import { AuthModule } from "./features/auth/auth.module";
import { EffectsModule } from '@ngrx/effects';
import { AuthEffects } from "./features/auth/store/auth.effects";
import { PlayerSelfEffects } from "./features/player-self/store/player-self.effects";
import { playerSelfReducer } from "./features/player-self/store/player-self.reducers";
import { authReducer } from "./features/auth/store/auth.reducers";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { NgxStripeModule } from "ngx-stripe";
import { DialogService } from "primeng/dynamicdialog";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastEffects } from "./shared/store/toast/toast.effects";
import { ToastModule } from "primeng/toast";
import { DialogModule } from "primeng/dialog";
import { ProgressBarModule } from "primeng/progressbar";
import { isPlatformBrowser, registerLocaleData } from "@angular/common";
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import { PortalModule } from "@angular/cdk/portal";
import { OverlayModule } from "@angular/cdk/overlay";
import { NgxTippyModule } from "ngx-tippy-wrapper";
import { popperVariation, provideTippyConfig, tooltipVariation } from "@ngneat/helipopper";
import { EmbedContextService } from "./shared/services/embed-context.service";
import * as Sentry from "@sentry/angular";

registerLocaleData(localeEn);
registerLocaleData(localeDe);

@NgModule({ declarations: [
        AppComponent,
    ],
    bootstrap: [AppComponent], imports: [EffectsModule.forRoot([AuthEffects, PlayerSelfEffects, ToastEffects]),
        EffectsModule.forFeature([AuthEffects, PlayerSelfEffects, ToastEffects]),
        StoreModule.forRoot(reducers, {
            metaReducers,
        }),
        StoreModule.forFeature(PLAYER_SELF_STATE, playerSelfReducer),
        StoreModule.forFeature(AUTH_STATE, authReducer),
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
        AngularSvgIconModule.forRoot(),
        AppConfigModule,
        BrowserModule,
        AppRoutingModule,
        AppLayoutModule,
        AppConfigModule,
        GraphQLModule,
        LegalModule,
        AuthModule,
        NgxsModule.forRoot([
            EventsState,
        ], { developmentMode: !environment.production }),
        NgxsLoggerPluginModule.forRoot({ disabled: environment.production }),
        NgxStripeModule.forRoot(),
        ConfirmDialogModule,
        ToastModule,
        DialogModule,
        ProgressBarModule,
    PortalModule,
    OverlayModule,
    NgxTippyModule,
  ], providers: [
        provideClientHydration(),
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
        {
            // Instantiates Sentry's TraceService eagerly so router navigations are traced.
            // Skipped during SSR, where Sentry is never initialized.
            provide: APP_INITIALIZER,
            useFactory: (platformId: Object, injector: Injector) => () => {
                if (isPlatformBrowser(platformId)) injector.get(Sentry.TraceService);
            },
            deps: [PLATFORM_ID, Injector],
            multi: true,
        },
        provideHttpClient(withFetch()),
        {
            provide: 'SocialAuthServiceConfig',
            // registering the provider makes SocialAuthService load the Google Identity Services script, which sets
            // cookies. Inside an embedding we therefore register none, so no consent is required. This has to use the
            // url derived isEmbedded, because the login template hides its button on the same flag and both have to
            // come out the same on the server and in the browser.
            useFactory: (embedContext: EmbedContextService): SocialAuthServiceConfig => ({
                autoLogin: false,
                providers: embedContext.isEmbedded ? [] : [{
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider(environment.gAuthClientId, {
                            scopes: ['https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/userinfo.profile'],
                            oneTapEnabled: false,
                        })
                    }]
            }),
            deps: [EmbedContextService],
        },
        DialogService,
        ConfirmationService,
        MessageService,
        provideHttpClient(withInterceptorsFromDi()),
        provideTippyConfig({
          defaultVariation: 'tooltip',
          variations: {
            tooltip: tooltipVariation,
            popper: popperVariation,
        },
    })
    ] })
export class AppModule { }
